import { Injectable } from '@angular/core';
import { AngularFirestore, DocumentChangeAction } from '@angular/fire/compat/firestore';
import { Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { SOP } from '../interfaces/sop.interface';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class SOPsService {
  constructor(
    private firestore: AngularFirestore,
    private auth: AuthService
  ) {}

  // Get all SOPs (for admins)
  getAllSOPs(): Observable<SOP[]> {
    return this.firestore.collection<SOP>('sops')
      .snapshotChanges()
      .pipe(
        map(actions => actions.map(a => {
          const data = a.payload.doc.data() as Omit<SOP, 'id'>;
          const id = a.payload.doc.id;
          return { id, ...data };
        }))
      );
  }

  // Get published SOPs only (for regular users)
  getPublishedSOPs(): Observable<SOP[]> {
    return this.firestore.collection<SOP>('sops', ref => 
      ref.where('status', '==', 'published')
    )
    .snapshotChanges()
    .pipe(
      map(actions => actions.map(a => {
        const data = a.payload.doc.data() as Omit<SOP, 'id'>;
        const id = a.payload.doc.id;
        return { id, ...data };
      }))
    );
  }

  // Get drafts (for admins only)
  getDrafts(): Observable<SOP[]> {
    return this.firestore.collection<SOP>('sops', ref => 
      ref.where('status', '==', 'draft')
    )
    .snapshotChanges()
    .pipe(
      map(actions => actions.map(a => {
        const data = a.payload.doc.data() as Omit<SOP, 'id'>;
        const id = a.payload.doc.id;
        return { id, ...data };
      }))
    );
  }

  // Create new SOP
  async createSOP(sop: Partial<SOP>): Promise<string> {
    const sopData = {
      ...sop,
      content: sop.content?.replace(/\n/g, '\n'),
      updatedAt: new Date()
    };
    return this.firestore.collection('sops').add(sopData)
      .then(ref => ref.id);
  }

  // Update existing SOP
  async updateSop(id: string, sop: Partial<SOP>): Promise<void> {
    const sopData = {
      ...sop,
      content: sop.content?.replace(/\n/g, '\n'),
      updatedAt: new Date()
    };
    return this.firestore.doc(`sops/${id}`).update(sopData);
  }

  // Get SOP by slug
  getSopBySlug(slug: string): Observable<SOP | null> {
    return this.auth.user$.pipe(
      switchMap(user => {
        if (!user) {
          console.log('No user logged in');
          return of(null);
        }
        
        return this.firestore
          .collection<SOP>('sops', ref => 
            ref.where('slug', '==', slug)
            .limit(1)
          )
          .snapshotChanges()
          .pipe(
            map(actions => {
              if (actions.length === 0) return null;
              const doc = actions[0];
              const data = doc.payload.doc.data() as Omit<SOP, 'id'>;
              return { id: doc.payload.doc.id, ...data };
            }),
            tap(sop => {
              if (sop) {
                console.log('Found SOP:', sop);
              } else {
                console.log('No SOP found with slug:', slug);
              }
            })
          );
      })
    );
  }

  // Get SOP by ID
  getSopById(id: string): Observable<SOP | null> {
    return this.firestore
      .collection<SOP>('sops')
      .doc(id)
      .snapshotChanges()
      .pipe(
        map(doc => {
          if (!doc.payload.exists) return null;
          const data = doc.payload.data() as Omit<SOP, 'id'>;
          return { id: doc.payload.id, ...data };
        })
      );
  }

  // Delete SOP
  async deleteSop(id: string): Promise<void> {
    try {
      await this.firestore.collection('sops').doc(id).delete();
    } catch (error) {
      console.error('Error deleting SOP:', error);
      throw error; // Re-throw the error to be handled by the component
    }
  }

  private generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
  }

  // Modify getSOPs to handle auth
  getSOPs(): Observable<SOP[]> {
    return this.auth.user$.pipe(
      switchMap(user => {
        if (!user) {
          console.log('No user logged in');
          return of([]);
        }
        
        return this.auth.isAdmin().pipe(
          switchMap(isAdmin => {
            console.log('Is admin:', isAdmin);
            
            if (isAdmin) {
              // Admins can see all SOPs
              return this.firestore.collection<SOP>('sops', ref => 
                ref.orderBy('updatedAt', 'desc')
              )
              .snapshotChanges()
              .pipe(
                map(actions => actions.map(a => {
                  const data = a.payload.doc.data() as Omit<SOP, 'id'>;
                  const id = a.payload.doc.id;
                  console.log('Fetched SOP:', { id, ...data });
                  return { id, ...data };
                }))
              );
            } else {
              // Regular users only see published SOPs
              return this.firestore.collection<SOP>('sops', ref => 
                ref.where('status', '==', 'published')
                .orderBy('updatedAt', 'desc')
              )
              .snapshotChanges()
              .pipe(
                map(actions => actions.map(a => {
                  const data = a.payload.doc.data() as Omit<SOP, 'id'>;
                  const id = a.payload.doc.id;
                  console.log('Fetched SOP:', { id, ...data });
                  return { id, ...data };
                }))
              );
            }
          })
        );
      })
    );
  }
} 